import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { LinearProgress, styled, Box, Typography } from '@mui/material';

// Custom Utilities
import {
    getFieldFormatters
} from '../../../utilities/DataSourceUtilities';

import {
    formatValue,
    formatTypographyColor
} from '../../../utilities/FormatUtilities';

import useDataSource from '../../../hooks/useDataSource';
import {useSelector} from "react-redux";

const PREFIX = 'SmartHeading';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    container: `${PREFIX}-container`
};

const StyledBox = styled(Box)((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: '100%',
		backgroundColor: theme.palette.background.default
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.container}`]: {
        display: 'flex',
        height: '100%',
        overflowY: 'auto',
        flexDirection: 'column'
    }
}));

export default function SmartHeading({id, configuration, onLoading, onLoaded}) {


    const {t, i18n} = useTranslation();

    // const persist = useSelector(state => state.persist);

    // https://jacobsengineering.sharepoint.com/sites/CPOV012053/Lists/Home/Home.aspx

    //     configuration: {
    //          dataSource: {
    //              name: <datasource>
    //              fields: {
    //                  value: <value field name from datasource>
    //              }
    //          },
    //          element: {
    //              styles: {
    //                  color: initial | inherit | primary | secondary | textPrimary | textSecondary | error | HEX code
    //                  variant: h1 | h2 | h3 | h4 | h5 | h6 | subtitle1 | subtitle2 | body1 | body2 | caption | button | overline | srOnly | inherit
    //                  justifyContent: flex-start | center | flex-end
    //                  textAlign: left | center | right
    //              }
    //          }
    //      }

    // DATASOURCE
    // -- DATASOURCE: Name
    const dataSourceName = configuration?.dataSource?.name ? configuration?.dataSource?.name : '';
    // -- DATASOURCE: Fields
    const valueField = configuration?.dataSource?.fields?.value ? configuration?.dataSource?.fields?.value : '';
    // ELEMENT
    const color = configuration?.element?.styles?.color ? configuration?.element?.styles?.color : 'initial';
    const variant = configuration?.element?.styles?.variant ? configuration?.element?.styles?.variant : 'h3';
    const justifyContent = configuration?.element?.styles?.justifyContent ? configuration?.element?.styles?.justifyContent : 'flex-start';
    const textAlign = configuration?.element?.styles?.textAlign ? configuration?.element?.styles?.textAlign : 'left';

    // App State
	const appUUID = useSelector(state => state.app.uuid);
	let dataSource = {
		fetching: false,
		fetched: false
	};
	let value;
	if (dataSourceName) {
		 dataSource = useDataSource(dataSourceName);
	} else {
		value = configuration?.element?.value !== undefined ? configuration.element.value : '';
	}

    // Component State
    const [heading, setHeading] = useState(null);
    const [headingTypographyColor, setHeadingTypographyColor] = useState({});
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);

    // Data
    useEffect(() => {

        if (dataSource.error) {
            // console.log('-----> SmartHeading:useEffect(): dataSource -- ERROR');
            // TODO: Error component

        } else if (dataSource.data && dataSource.fetched) {
			// console.log('-----> SmartHeading:useEffect(): dataSource -- FETCHED DATA');

			// Get formatter from datasource
			const formatters = getFieldFormatters(dataSource, valueField);

			// Set component state
			setHeading(formatValue(dataSource.data[0][valueField], formatters, null, false));
			setHeadingTypographyColor(formatTypographyColor(color));
		} else if (value) {
			if (value.indexOf("$uuid") > -1) {
				value = value.replace("$uuid", appUUID);
				setHeading(value);
				setShowProgress(true);
			} else {
				setHeading(value);
			}
			setHeadingTypographyColor(formatTypographyColor(color));
		} else {
            // console.log('-----> SmartHeading:useEffect(): dataSource -- OTHER');

            // Set component state
            setHeading(null);
            // TODO: Error component
        }

    }, [dataSource.data, value]);

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

    return (
        <StyledBox className={classes.root}>
            {showProgress && dataSource.fetching &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
            <Box className={classes.linearProgress}></Box>
            }
            <Box className={classes.container} style={{justifyContent: justifyContent, textAlign: textAlign}}>
                <Typography color={headingTypographyColor.component} variant={variant} style={{color: headingTypographyColor.style}}>
                    {t(heading)}
                </Typography>
            </Box>
        </StyledBox>
    );
}
